import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { OCCUPANCY_TONE_COLOR, SPECIAL_LABEL_KEY, formatHours, formatPercent, formatWholePercent, occupancyTone } from './loadCapacityFormat'

const SummaryCell = ({ label, value, caption = null, color = 'text.primary', muted = false, testId }) => (
  <Stack spacing={0.25} sx={{ flex: '0 0 auto', minWidth: '6.5rem' }} data-testid={testId}>
    <Typography variant='caption' color='text.secondary' component='div' sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>
      {label}
    </Typography>
    <Typography variant='h6' component='div' color={muted ? 'text.disabled' : color} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {caption !== null
      ? (
        <Typography variant='caption' color='text.secondary' component='div' data-testid={testId ? `${testId}-caption` : undefined}>
          {caption}
        </Typography>
        )
      : null}
  </Stack>
)

/**
 * CU8 -- the one-line summary of the totals over the columns on screen: load, OEE capacity, free hours, excess
 * (RN28), OEE loss (only while it is drawn), the occupancy of the range, how many of the shown
 * periods are saturated (RN25 per column), and the load of the special column. Every figure is a sum
 * of the server's own per-column numbers (see `summarizeBuckets`); the cells that read zero are
 * simply not shown, so the strip only ever calls out what is actually there.
 */
const LoadCapacitySummary = ({ summary, showOeeLoss = true }) => {
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  if (!summary || summary.periodCount === 0) {
    return null
  }
  const tone = occupancyTone(summary.occupancyPercent)
  const saturatedColor = summary.saturatedPeriods > 0 ? 'error.main' : 'text.primary'

  return (
    <Box
      sx={{ padding: '0.75rem 1rem', borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover', overflowX: 'auto' }}
      data-testid='load-capacity-summary'
    >
      <Stack direction='row' spacing={4} flexWrap='nowrap' data-testid='load-capacity-summary-line'>
        <SummaryCell label={t_('Load hours')} value={`${formatHours(summary.loadHours, locale)} h`} testId='load-capacity-summary-load' />
        <SummaryCell
          label={t_('OEE capacity')}
          value={`${formatHours(summary.oeeCapacityHours, locale)} h`}
          caption={summary.oeePercentOfCalendar === null ? null : t_('{{percent}} % of the calendar', { percent: formatWholePercent(summary.oeePercentOfCalendar, locale) })}
          testId='load-capacity-summary-capacity'
        />
        <SummaryCell
          label={t_('Available')}
          value={`${formatHours(summary.availableHours, locale)} h`}
          caption={summary.availablePercentOfCapacity === null ? null : t_('{{percent}} % of the OEE capacity', { percent: formatWholePercent(summary.availablePercentOfCapacity, locale) })}
          testId='load-capacity-summary-available'
        />
        {summary.excessHours > 0
          ? (
            <SummaryCell
              label={t_('Excess')}
              value={`+${formatHours(summary.excessHours, locale)} h`}
              caption={summary.excessPercentOfCapacity === null ? null : t_('{{percent}} % of the OEE capacity', { percent: formatWholePercent(summary.excessPercentOfCapacity, locale) })}
              color='error.main'
              testId='load-capacity-summary-excess'
            />
            )
          : null}
        {showOeeLoss && summary.oeeLossHours > 0
          ? (
            <SummaryCell
              label={t_('OEE loss')}
              value={`${formatHours(summary.oeeLossHours, locale)} h`}
              caption={summary.oeePercentOfCalendar === null ? null : t_('{{percent}} % of the calendar', { percent: formatWholePercent(100 - summary.oeePercentOfCalendar, locale) })}
              muted
              testId='load-capacity-summary-oee-loss'
            />
            )
          : null}
        <SummaryCell
          label={t_('Occupancy')}
          value={summary.occupancyPercent === null ? t_('Not available') : `${formatPercent(Math.round(summary.occupancyPercent), locale)} %`}
          color={OCCUPANCY_TONE_COLOR[tone]}
          testId='load-capacity-summary-occupancy'
        />
        <SummaryCell
          label={t_('Saturated periods')}
          value={t_('{{saturated}} of {{total}}', { saturated: summary.saturatedPeriods, total: summary.periodCount })}
          color={saturatedColor}
          testId='load-capacity-summary-saturated'
        />
        {summary.specialType
          ? (
            <SummaryCell
              label={t_(SPECIAL_LABEL_KEY[summary.specialType])}
              value={`${formatHours(summary.specialLoadHours, locale)} h`}
              color='error.main'
              testId='load-capacity-summary-special'
            />
            )
          : null}
      </Stack>
    </Box>
  )
}

export default LoadCapacitySummary
